import QueueStatusBadge from '@components/Common/QueueStatusBadge';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import { Box, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import './SkillProfilePanel.scss';

/**
 * What the resume-extraction pipeline knows about a candidate: which of their
 * skills the market wants, which categories they cover, and what would unlock
 * the most jobs next.
 *
 * Both actions are fire-and-forget: they enqueue work and the queue badge
 * reports on it, because a recompute takes far longer than a click should
 * block for.
 */

interface PresentSkill {
  skill: string;
  marketDemandScore: number;
  jobsMatching: number;
}

interface SkillGap {
  skill: string;
  roiScore: number;
  jobsUnlocked: number;
  difficulty: string;
}

interface CategoryFit {
  category: string;
  matchPct: number;
  jobsAvailable: number;
}

interface SkillProfile {
  presentSkills: PresentSkill[];
  skillGaps: SkillGap[];
  topRecommendedSkills: string[];
  marketFitScore: number;
  categoryFit: CategoryFit[];
  computedAt: string;
}

interface SkillProfilePanelProps {
  jobSeekerId: string;
}

const QUEUE_JOBS = ['EXTRACT_RESUME', 'REFRESH_SKILL_PROFILE'];

/** Demand at or above this reads as "the market wants this skill". */
const DEMANDED = 0.3;

const SkillProfilePanel: React.FC<SkillProfilePanelProps> = ({ jobSeekerId }) => {
  const [profile, setProfile] = useState<SkillProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<'refresh' | 'extract' | null>(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/skill-profile/${jobSeekerId}`);
      setProfile(data);
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [jobSeekerId]);

  useEffect(() => {
    if (jobSeekerId) fetchProfile();
  }, [jobSeekerId, fetchProfile]);

  /** Recompute from signals that have already been extracted. */
  const triggerRefresh = async () => {
    setBusy('refresh');
    try {
      await axios.post(`/api/skill-profile/${jobSeekerId}/refresh`);
    } finally {
      setBusy(null);
    }
  };

  /** Run the whole pipeline: resume -> signals -> profile. */
  const triggerExtraction = async () => {
    setBusy('extract');
    try {
      await axios.post('/api/extract/resume', { jobSeekerId });
    } finally {
      setBusy(null);
    }
  };

  if (loading || busy) {
    /*
     * A spinner, deliberately, not SkeletonLoader. A skeleton stands in for content that is
     * arriving; this stands beside a sentence about an operation that is running and how long it
     * takes. Swapping it would drop the sentence, which is the useful half.
     */
    return (
      <Box className="cc-skill__busy">
        <CircularProgress size={22} />
        <span className="cc-skill__note">
          {busy === 'extract'
            ? 'Extracting resume signals. This takes about 30 seconds.'
            : busy === 'refresh'
              ? 'Computing skill profile…'
              : 'Loading…'}
        </span>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box className="cc-skill__empty">
        <Box className="cc-skill__head">
          <span className="cc-skill__note">
            No skill profile yet. Extract resume signals to generate one.
          </span>
          <QueueStatusBadge entityId={jobSeekerId} jobTypes={QUEUE_JOBS} onDrained={fetchProfile} />
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<PsychologyOutlinedIcon />}
          onClick={triggerExtraction}
        >
          Extract signals
        </Button>
      </Box>
    );
  }

  const marketPct = Math.round((profile.marketFitScore ?? 0) * 100);
  const skills = profile.presentSkills ?? [];
  const shownSkills = skills.slice(0, 10);
  const extraSkills = Math.max(0, skills.length - shownSkills.length);
  const categories = (profile.categoryFit ?? []).slice(0, 5);
  const gaps = (profile.skillGaps ?? []).slice(0, 4);

  const barClass = (pct: number) => (pct >= 50 ? '' : pct >= 25 ? ' is-mid' : ' is-low');

  return (
    <Box className="cc-skill">
      <Box className="cc-skill__head">
        <h3 className="cc-skill__title">Skill profile</h3>
        <QueueStatusBadge entityId={jobSeekerId} jobTypes={QUEUE_JOBS} onDrained={fetchProfile} />
        <Tooltip title="Recompute skill profile">
          <IconButton
            aria-label="Recompute skill profile"
            className="cc-skill__refresh"
            size="small"
            onClick={triggerRefresh}
          >
            <AutorenewIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className="cc-skill__cols">
        {/* Market fit and the skills already detected */}
        <div>
          <div className="cc-skill__fit">
            <span className={`cc-skill__fit-value${marketPct >= 60 ? '' : ' is-low'}`}>
              {marketPct}%
            </span>
            <span className="cc-skill__fit-unit">market fit</span>
          </div>
          <div className="cc-skill__bar">
            <div
              className={`cc-skill__bar-fill${marketPct >= 60 ? '' : ' is-low'}`}
              style={{ width: `${Math.min(marketPct, 100)}%` }}
            />
          </div>

          <span className="cc-skill__col-label cc-skill__col-label--spaced">
            Skills detected ({skills.length})
          </span>
          {shownSkills.length === 0 ? (
            <span className="cc-skill__note">
              No skills detected. This candidate&apos;s profile may be incomplete.
            </span>
          ) : (
            <div className="cc-skill__chips">
              {shownSkills.map((s) => (
                <Tooltip
                  key={s.skill}
                  title={`${s.jobsMatching} jobs require this skill (demand ${Math.round(s.marketDemandScore * 100)}%)`}
                >
                  <span
                    className={`cc-skill__chip${s.marketDemandScore >= DEMANDED ? ' is-demanded' : ''}`}
                  >
                    {s.skill}
                  </span>
                </Tooltip>
              ))}
              {extraSkills > 0 && <span className="cc-skill__chip">+{extraSkills} more</span>}
            </div>
          )}
        </div>

        {/* Category coverage */}
        <div>
          <span className="cc-skill__col-label">Category coverage</span>
          {categories.length === 0 ? (
            <span className="cc-skill__note">No category data available.</span>
          ) : (
            <div className="cc-skill__categories">
              {categories.map((c) => (
                <div key={c.category}>
                  <div className="cc-skill__category-head">
                    <span className="cc-skill__category-name">{c.category}</span>
                    <span className="cc-skill__category-pct">{c.matchPct}%</span>
                  </div>
                  <div className="cc-skill__bar">
                    <div
                      className={`cc-skill__bar-fill${barClass(c.matchPct)}`}
                      style={{ width: `${Math.min(c.matchPct, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* What to learn next */}
        <div>
          <span className="cc-skill__col-label">
            <EmojiObjectsOutlinedIcon className="cc-skill__col-icon" />
            Suggested to learn
          </span>
          {gaps.length === 0 ? (
            <span className="cc-skill__note">No skill gaps identified.</span>
          ) : (
            <div className="cc-skill__gaps">
              {gaps.map((g) => (
                <div key={g.skill} className="cc-skill__gap">
                  <div className="cc-skill__gap-copy">
                    <span className="cc-skill__gap-skill">{g.skill}</span>
                    <span className="cc-skill__gap-meta">unlocks +{g.jobsUnlocked} jobs</span>
                  </div>
                  <span className={`cc-skill__difficulty is-${(g.difficulty || '').toLowerCase()}`}>
                    {g.difficulty}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default SkillProfilePanel;
